import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { GuestForm } from "Bands/components";
import {
  BAND_ACTIONS,
  IEventGuest,
  IEventGuestParams,
  IEventGuestStatus,
  IGuestlistBlock,
  IGuestlistBlockType,
} from "Bands/types";
import {
  getGuestStatus,
  useClearActionMetaOnUnMount,
  useGuestlistNumberError,
} from "Bands/utils";
import { copy } from "shared/assets";
import { Message, Modal } from "shared/components";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { dataMetaSelector, editEventGuest } from "Bands/state";
import { mapUserPermissions } from "shared/utils";
import { API_ENDPOINTS } from "shared/constants";
import { addTemporaryToast } from "shared/components/Toast";
import { SmallText, StatusText } from "shared/styles";

const editCopy = copy.guestlistBlock.guests.edit;

interface EditGuestModalProps {
  block: IGuestlistBlock;
  guest: IEventGuest;
  handleClose: () => void;
}

export const EditGuestModal: React.FC<EditGuestModalProps> = ({
  block: {
    id: blockId,
    ticketAndPassTypes: ticketAndPasses,
    maxTicketsPerEntry,
    guestBlockType,
  },
  guest,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editEventGuest)
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.editEventGuest);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      handleClose();

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editCopy.messages.success,
        })
      );
    }
  }, [apiMeta?.status, dispatch, handleClose]);

  const handleSubmit = (newGuest: IEventGuestParams) => {
    const { ticketAndPassTypes, ...rest } = newGuest;

    if (!ticketAndPassTypes) return;

    dispatch(
      editEventGuest({
        id: guest.id,
        data: {
          ...rest,
          ticketAndPassTypes: ticketAndPassTypes.map((id) =>
            API_ENDPOINTS.EVENT_TICKET_PASS(id)
          ),
        },
        blockId,
      })
    );
  };

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        "data-testid": "submit_edit_guest",
      },
      secondary: {
        onClick: handleClose,
      },
    }),
    [handleClose]
  );

  const { error } = useGuestlistNumberError(apiMeta?.error);

  const defaultValues: IEventGuestParams = useMemo(
    () => ({
      ...guest,
      ticketAndPassTypes: guest.ticketAndPassTypes.map(({ id }) => id),
    }),
    [guest]
  );

  const userCanChangeStatus = useMemo(
    () => mapUserPermissions(guest.permissions || []).CHANGE_ENTRY_STATUS,
    [guest]
  );

  const statusMessage = useMemo(() => {
    if (userCanChangeStatus) return null;

    const { status: guestStatus } = getGuestStatus(guest.status);
    const currentStatusMessage = (
      <SmallText>
        {editCopy.messages.currentStatus}:{" "}
        <StatusText status={guestStatus}>{guest.status}</StatusText>
      </SmallText>
    );

    if (guestBlockType !== IGuestlistBlockType.approval_needed)
      return currentStatusMessage;

    let extraMessage = null;

    if (guest.status === IEventGuestStatus.approved) {
      extraMessage = (
        <SmallText>
          {editCopy.messages.increaseMessage}:{" "}
          <StatusText status={IStatus.warning}>
            {IEventGuestStatus.pending}
          </StatusText>
        </SmallText>
      );
    }

    if (guest.status === IEventGuestStatus.denied) {
      extraMessage = (
        <SmallText>
          {editCopy.messages.changeMessage}:{" "}
          <StatusText status={IStatus.warning}>
            {IEventGuestStatus.pending}
          </StatusText>
        </SmallText>
      );
    }

    return (
      <>
        {currentStatusMessage}
        <br />
        {extraMessage}
      </>
    );
  }, [guest.status, guestBlockType, userCanChangeStatus]);

  return (
    <Modal
      title={editCopy.title}
      handleClose={handleClose}
      data-testid="edit_capacity_modal"
    >
      <GuestForm
        hideStatus={!userCanChangeStatus}
        defaultValues={defaultValues}
        handleSubmit={handleSubmit}
        buttons={buttons}
        apiStatus={apiMeta?.status}
        apiError={apiMeta?.error}
        error={error}
        maxTicketsPerEntry={maxTicketsPerEntry}
        ticketAndPasses={ticketAndPasses}
      >
        {statusMessage && (
          <>
            <Message>{statusMessage}</Message>
            <br />
          </>
        )}
      </GuestForm>
    </Modal>
  );
};
